import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { ErrorMessage } from 'app/core/error-handler/constants';
import { Error } from 'app/core/error-handler/models';
import { createErrorRaised } from 'app/core/error-handler/state/actions';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { NotificationBulkSendInterface, NotificationInterface, NotificationTemplateInterface } from '../models';
import { NotificationRestConnectorService } from '../services/notification-rest-connector.service';
import { NotificationServiceFilters } from '../services/notification.service';

import { LoadNotifications, LoadNotificationsCompleted, NotificationActionType, SendNotifications, SendNotificationsCompleted, UpdateNotification, UpdateNotificationCompleted, UpdateNotificationsReadState } from './actions';
import { DeleteNotificationTemplate, DeleteNotificationTemplateComplete } from './actions/notification-delete.action';
import { LoadNotificationTemplatesCompleted } from './actions/notification-load.action';
import { CreateNotificationTemplate, NotificationBulkUpdatePayload, UpdateNotificationsReadStateCompleted, UpdateNotificationTemplate, UpdateNotificationTemplateComplete } from './actions/notification-update.action';

@Injectable()
export class NotificationEffects {
  public source: string = 'NotificationEffects';


  // Load all notifications
  @Effect()
  public loadRecommendations$: Observable<Action> = this._action$
    .pipe(
      ofType(NotificationActionType.LoadNotifications),
      map((action: LoadNotifications) => action.payload),
      switchMap((notificationFilters: NotificationServiceFilters) => {
        return this._notificationRestConnector.loadAll(notificationFilters).pipe(
          map((notifications: Notification[]) => new LoadNotificationsCompleted(notifications)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }),
    );

  // Update a notification
  @Effect()
  public updateNotification$: Observable<Action> = this._action$
  .pipe(
    ofType(NotificationActionType.UpdateNotification),
    map((action: UpdateNotification) => action.payload),
    switchMap((notification: NotificationInterface) => {
      return this._notificationRestConnector.update(notification).pipe(
        map((notification: NotificationInterface) => new UpdateNotificationCompleted(notification)),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }),
  );

  // Bulk update read/unread
  @Effect()
  public updateNotificationReadState$: Observable<Action> = this._action$
  .pipe(
    ofType(NotificationActionType.UpdateNotificationsReadState),
    map((action: UpdateNotificationsReadState) => action.payload),
    switchMap((payload: NotificationBulkUpdatePayload) => {
      return this._notificationRestConnector.updateManyReadUnread(payload.notifications, payload.readState).pipe(
        map((notifications: NotificationInterface[]) => new UpdateNotificationsReadStateCompleted(notifications)),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }),
  );

  @Effect()
  public loadNotificationTemplates$: Observable<Action> = this._action$
  .pipe(
    ofType(NotificationActionType.LoadNotificationTemplates),
    switchMap(()=> {
      return this._notificationRestConnector.loadNotificationTemplates()
      .pipe(
        map((notificationTemplates: NotificationTemplateInterface[]) =>
          new LoadNotificationTemplatesCompleted(notificationTemplates)),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }),
  );

  @Effect()
  public sendNotificationsToUsers$: Observable<Action> = this._action$
  .pipe(
    ofType(NotificationActionType.SendNotifications),
    map((action: SendNotifications) => action.payload),
    switchMap((payload: NotificationBulkSendInterface) => {
      return this._notificationRestConnector.sendBulkNotifications(payload)
      .pipe(
        map((recipients: number[]) =>
          new SendNotificationsCompleted(recipients)),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
        );
    }),
    );

  @Effect()
  public createNotificationTemplate$: Observable<Action> = this._action$
  .pipe(
    ofType(NotificationActionType.CreateNotificationTemplate),
    map((action: CreateNotificationTemplate) => action.payload),
    switchMap((templateToCreate: NotificationTemplateInterface) => {
      return this._notificationRestConnector.createNotificationTemplate(templateToCreate)
      .pipe(
        map((createdTemplate: NotificationTemplateInterface) => new UpdateNotificationTemplateComplete(createdTemplate)),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }),
  );

  @Effect()
  public updateNotificationTemplate$: Observable<Action> = this._action$
  .pipe(
    ofType(NotificationActionType.UpdateNotificationTemplate),
    map((action: UpdateNotificationTemplate) => action.payload),
    switchMap((templateToUpdate: NotificationTemplateInterface) => {
      return this._notificationRestConnector.updateNotificationTemplate(templateToUpdate)
      .pipe(
        map((updatedTemplate: NotificationTemplateInterface) => new UpdateNotificationTemplateComplete(updatedTemplate)),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }),
  );

  @Effect()
  public deleteNotificationTemplate$: Observable<Action> = this._action$
  .pipe(
    ofType(NotificationActionType.DeleteNotificationTemplate),
    map((action: DeleteNotificationTemplate) => action.payload),
    switchMap((templateToDelete: NotificationTemplateInterface) => {
      return this._notificationRestConnector.deleteNotificationTemplate(templateToDelete)
      .pipe(
        map((deletedTemplate: NotificationTemplateInterface) => new DeleteNotificationTemplateComplete(deletedTemplate)),
        catchError((err: any) => of(createErrorRaised(new Error({
          message: ErrorMessage.unexpected,
          errorResponse: err,
          source: this.source,
        })))),
      );
    }),
  );

  constructor(private readonly _action$: Actions, private readonly _notificationRestConnector: NotificationRestConnectorService) {}
}
